import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Stack,
  Tag,
  Text,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronRight, FiExternalLink, FiFileText } from "react-icons/fi";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { PublicationChannel, SourcingEventStatus, SourcingEventV2Dto } from "../../../autogen/bff-api";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { Explanation } from "../../../common/support/Explanation/Explanation";
import { urls } from "../../../urls";
import {
  formatEstimatedValue,
  getContractNatureLabel,
  getProcedureLabel,
} from "../../sourcing-events/portal/utils/displayFormatters";
import { LabelValueGrid } from "../../sourcing-events/portal/view-single/LabelValueGrid";
import { DevFastForwardMenu } from "./DevFastForwardMenu";
import { GoToContractButton } from "./GoToContractButton";
import { SourcingEventV2StatusTag } from "./SourcingEventV2StatusTag";
import { competitionStage, effectiveBidDeadline, type CompetitionStage } from "./qualification/stage";
import { buildDoffinNoticeUrl } from "./doffinNoticeUrl";

const DASH = "–";

const personName = (p: { firstName?: string | null; lastName?: string | null; email: string }) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

// t('Request phase') t('Qualification phase') t('Tender phase')
const STAGE_LABEL_KEYS: Record<CompetitionStage, string> = {
  REQUESTS_OPEN: "Request phase",
  QUALIFICATION: "Qualification phase",
  TENDERING: "Tender phase",
};

const AWARDABLE_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.AwardDecision,
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
];

interface Props {
  event: SourcingEventV2Dto;
  onViewPublic?: () => void;
  responsibleEditControl?: ReactNode;
}

export const EventHeader = ({ event, onViewPublic, responsibleEditControl }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const canGenerateProtocol = AWARDABLE_STATUSES.includes(event.status);
  const { responsiblePerson } = event;
  const { eventData, publicContext } = event;
  const value = publicContext.estimatedValue;
  const doffinReference = publicContext.publications?.find(
    (p) => p.channel === PublicationChannel.Doffin && p.externalId,
  )?.externalId;
  const stage = competitionStage(event);
  const bidDeadline = effectiveBidDeadline(event);
  const isTwoStage = stage != null;

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p={{ base: "4", md: "6" }}>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "flex-start" }}
        gap="3"
        mb="4"
      >
        <Breadcrumb
          separator={<Icon as={FiChevronRight} color="smMuted" boxSize="3" />}
          fontSize="sm"
          flex="1"
          minW="0"
        >
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={urls.v2.sourcingEvents.viewAll} color="smPrimary">
              {t("All procurements")}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="smMuted" isTruncated maxW={{ base: "220px", md: "360px", lg: "520px" }}>
              {eventData.title}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <HStack spacing="2" flexShrink={0} justify={{ base: "flex-start", md: "flex-end" }} flexWrap="wrap">
          <DevFastForwardMenu event={event} />
          <GoToContractButton event={event} />
          <Button size="sm" variant="outline" leftIcon={<Icon as={FiExternalLink} />} onClick={onViewPublic}>
            {t("View as supplier")}
          </Button>
          <Explanation text={t("Complete the evaluation first")} enabled={!canGenerateProtocol}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<Icon as={FiFileText} />}
              isDisabled={!canGenerateProtocol}
              onClick={() => navigate(urls.v2.sourcingEvents.protocol.url(event.id))}
            >
              {t("Generate protocol")}
            </Button>
          </Explanation>
        </HStack>
      </Flex>

      <Stack spacing="2" mb="5">
        <Heading as="h1" size="lg" color="smText" lineHeight="1.15">
          {eventData.title || t("Untitled")}
        </Heading>
        <HStack spacing="2" align="center" flexWrap="wrap">
          <SourcingEventV2StatusTag status={event.status} />
          {stage && event.status === SourcingEventStatus.Published && (
            <Tag colorScheme="purple" variant="subtle">
              {t(STAGE_LABEL_KEYS[stage])}
            </Tag>
          )}
        </HStack>
      </Stack>

      <LabelValueGrid
        rows={[
          {
            label: t("Responsible buyer"),
            value: (
              <HStack spacing="2" flexWrap="wrap">
                <Text as="span">{responsiblePerson ? personName(responsiblePerson) : DASH}</Text>
                {responsibleEditControl}
              </HStack>
            ),
          },
          ...(isTwoStage
            ? [
                {
                  label: t("Deadline for requests to participate"),
                  value: eventData.requestDeadline ? displayDate(eventData.requestDeadline) : DASH,
                },
              ]
            : []),
          {
            label: t("Deadline"),
            value: bidDeadline ? displayDate(bidDeadline) : DASH,
          },
          {
            label: t("Question deadline"),
            value: event.questionDeadline ? displayDate(event.questionDeadline) : DASH,
          },
          {
            label: t("Procedure"),
            value: publicContext.procedureType ? getProcedureLabel(publicContext.procedureType, t) : DASH,
          },
          {
            label: t("Contract type"),
            value: publicContext.contractNature ? getContractNatureLabel(publicContext.contractNature, t) : DASH,
          },
          {
            label: t("Estimated value"),
            value: value?.value != null ? formatEstimatedValue(value.value, value.currency) : DASH,
          },
          ...(doffinReference
            ? [
                {
                  label: t("Doffin reference"),
                  value: (
                    <Link
                      href={buildDoffinNoticeUrl(doffinReference)}
                      isExternal
                      rel="noopener noreferrer"
                      color="smPrimary"
                      display="inline-flex"
                      alignItems="center"
                      gap="1"
                    >
                      {doffinReference}
                      <Icon as={FiExternalLink} boxSize="3" />
                    </Link>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Box>
  );
};
